import { useForm } from '@mantine/form';
import { useDebouncedCallback } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import {
  useNavigate,
  useParams,
  useRouter,
  useSearch
} from '@tanstack/react-router';
import type { AxiosProgressEvent } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import {
  useArtistProfileVideoListQuery,
  useDeleteVideoItemMutation,
  useUploadVideoMutation
} from '@/shared/api/requests';

import { ArtistVideoTag } from '../../../../../../../../@types/types';

type ArtistVidoeForm = {
  dance: {
    file: File | null | string;
    title: string;
    id: null | string;
    thumbanilPath: null | string;
    description: string;
    tag: ArtistVideoTag;
    ArtistProfileId: string;
  };
  walk: {
    file: File | null | string;
    title: string;
    id: null | string;
    thumbanilPath: null | string;
    description: string;
    tag: ArtistVideoTag;
    ArtistProfileId: string;
  };
};

type ArtistVidoeFormKey = keyof ArtistVidoeForm;

export const useArtistOnBoardVideo = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate({ from: '/artist/$id/onboard/$step' });
  const { id: profileId } = useParams({ from: '/artist/$id' });
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/artist/$id/_onboard/onboard/$step' });
  const router = useRouter();

  const uploadVideoMutation = useUploadVideoMutation({});

  const profileVideo = useArtistProfileVideoListQuery({ profileId });
  const deleteVideoMutation = useDeleteVideoItemMutation();
  const progressRef = useRef<Record<string, number>>({});
  const [progress, setProgress] = useState(0);

  const form = useForm<ArtistVidoeForm>({
    initialValues: {
      dance: {
        file: null,
        id: null,
        thumbanilPath: '',
        title: 'Test video for strip dance',
        description: 'Test video used for strip dance',
        tag: ArtistVideoTag.StripDance,
        ArtistProfileId: profileId
      },
      walk: {
        file: null,
        id: null,
        thumbanilPath: '',
        title: 'Test video for walk',
        description: 'Test video used for walk',
        tag: ArtistVideoTag.Walk,
        ArtistProfileId: profileId
      }
    }
  });

  useEffect(() => {
    if (profileVideo.isSuccess && profileVideo.data?.data.length) {
      const stripDance = profileVideo.data.data.filter(
        (item) => item.tag === ArtistVideoTag.StripDance
      );
      const walk = profileVideo.data.data.filter(
        (item) => item.tag === ArtistVideoTag.Walk
      );

      if (stripDance.length) {
        form.setFieldValue('dance.file', stripDance[0].filePath);
        form.setFieldValue('dance.id', stripDance[0].id.toString());
        form.setFieldValue('dance.thumbanilPath', stripDance[0].thumbnailPath);
      }
      if (walk.length) {
        form.setFieldValue('walk.file', walk[0].filePath);
        form.setFieldValue('walk.id', walk[0].id.toString());
        form.setFieldValue('walk.thumbanilPath', walk[0].thumbnailPath);
      }
    }
  }, [profileVideo.data?.data]);

  const next = () => {
    if (search.from !== undefined) {
      router.history.back();
    }
    navigate({
      to: '/artist/$id/onboard/$step',
      params: {
        step: 'about'
      }
    });
  };

  const isFormValid = React.useMemo(
    () => Boolean(form.getValues().dance.file && form.getValues().walk.file),
    [form.getValues().dance.file, form.getValues().walk.file]
  );
  const debouncedUpdateCommonProgress = useDebouncedCallback(() => {
    const values = Object.values(progressRef.current);
    const commonProgress = values.length
      ? Math.round(values.reduce((sum, val) => sum + val, 0) / values.length)
      : 0;
    setProgress(commonProgress);
  }, 100);

  const updateProgress = (key: string, loaded: number, total: number) => {
    const newPercentage = Math.round((loaded * 100) / total);
    progressRef.current[key] = newPercentage;
    debouncedUpdateCommonProgress();
  };

  const onSubmit = form.onSubmit(async (values) => {
    form.setSubmitting(true);

    try {
      for (const [key, value] of Object.entries(values)) {
        if (typeof value.file !== 'string' && value.file !== null) {
          const formData = new FormData();

          formData.append('File', value.file);
          formData.append('Title', value.title);
          formData.append('Description', value.description);
          formData.append('Tag', value.tag);
          formData.append('ArtistProfileId', profileId);

          // Aștepți upload-ul înainte să treci la următorul
          await uploadVideoMutation.mutateAsync(
            {
              params: {
                formData
              },
              config: {
                onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                  if (progressEvent && progressEvent.total) {
                    updateProgress(
                      key,
                      progressEvent.loaded,
                      progressEvent.total
                    );
                  }
                }
              }
            },
            {
              onSuccess: () => {
                notifications.show({
                  title: formatMessage({
                    id: 'notifications.successDataUpdate'
                  }),
                  ...notificationStyleConfig
                });
              },
              onError: () => {
                notifications.show({
                  title: formatMessage({
                    id: 'notifications.errorOnDataUpdate'
                  }),
                  ...notificationStyleConfig
                });
              }
            }
          );

          // Ștergi video-ul vechi doar după ce upload-ul a reușit
          if (value.id !== null) {
            await deleteVideoMutation.mutateAsync({
              params: {
                videoId: value.id.toString()
              }
            });
          }
        }
      }
      await queryClient.invalidateQueries({
        queryKey: ['ArtistProfileVideoList', profileId]
      });
      next();
    } finally {
      form.setSubmitting(false);
    }
  });

  const onDeleteVideo = async (
    videoId: string | null,
    tag: ArtistVidoeFormKey
  ) => {
    if (videoId) {
      await deleteVideoMutation.mutateAsync(
        {
          params: {
            videoId
          }
        },
        {
          onSuccess: () => {
            form.setFieldValue(`${tag}.file`, null);
            form.setFieldValue(`${tag}.id`, null);

            queryClient.invalidateQueries({
              queryKey: ['ArtistProfileVideoList', profileId]
            });
          }
        }
      );
    } else {
      form.setFieldValue(`${tag}.file`, null);
      form.setFieldValue(`${tag}.id`, null);
    }
  };

  const queries = [profileVideo];

  const isLoading = queries.some((q) => q.isLoading);

  return {
    state: { isLoading, form, isFormValid, percentage: progress },
    functions: {
      next,
      onSubmit,
      onDeleteVideo
    }
  };
};
